import {
  DocumentType,
  EscrowAnalysisDocument,
  Form1098Document,
  GeneralDocument,
  PayoffDocument,
  PortalDocumentFragment,
  PortalPaymentDueFragment,
  UploadDocument,
} from '@willow/graphql-iso/src/portal';
import { EMAIL_TITLES } from '@willow/shared-iso';
import {
  assertNever,
  dateFormat,
  DocumentDisplayItem,
  getStatementItems,
  gqlIsoDateFormat,
  paymentAmountFormat,
  sortDocumentsByDate,
} from '@willow/shared-web';

import { TDocumentTypeOption } from '../../../pages/documents/form';

export function getTableItems(
  documentType: TDocumentTypeOption,
  paymentsDue: PortalPaymentDueFragment[],
  documents: PortalDocumentFragment[],
  transferDate: Date | undefined,
  interestDueAtPayoff: boolean,
): DocumentDisplayItem[] {
  const formattedStatements = getStatementItems(
    paymentsDue.map((p) => ({ ...p, paymentDate: new Date(p.paymentDate) })),
    transferDate,
    interestDueAtPayoff,
  );
  const formattedDocuments = getDocumentItems(documents);

  // Filter by document type
  const filteredStatements = formattedStatements.filter((s) => {
    return documentType === 'current' ? !s.isArchived : s.isArchived;
  });
  const filteredDocuments = formattedDocuments.filter((d) => {
    const outdatedDocument = d.callouts.includes('Expired') || d.isArchived;
    return documentType === 'current' ? !outdatedDocument : outdatedDocument;
  });

  // Combine + sort the items
  const combinedDocuments = [...filteredStatements, ...filteredDocuments];
  const sortedDocuments = sortDocumentsByDate(combinedDocuments);

  return sortedDocuments.map((document) => {
    return {
      ...document,
      date: dateFormat(document.date.toString()),
    };
  });
}

function getDocumentItems(documents: PortalDocumentFragment[]): DocumentDisplayItem[] {
  return documents.map((document) => {
    const { type, id, createdAt, version, isArchived } = document;

    let title;
    let subtitle: string | undefined;
    let callouts: string[] = [];
    let amount: string | undefined;
    let sortByDate: Date = createdAt;
    let notificationType: DocumentDisplayItem['notificationType'] = 'document';

    version > 1 && callouts.push('Revised');

    switch (type) {
      case DocumentType.Goodbye:
        title = 'Goodbye Letter';
        break;
      case DocumentType.Payoff:
        title = `Payoff Statement`;
        const { isExpired: payoffIsExpired, payoffAmount, expirationDate } = document as PayoffDocument;
        payoffIsExpired && callouts.push('Expired');
        amount = paymentAmountFormat(payoffAmount);
        subtitle = expirationDate ? `Good through ${gqlIsoDateFormat(expirationDate)}` : undefined;
        break;
      case DocumentType.PaidInFull:
        const { amount: paidAmount } = document as GeneralDocument;
        title = 'Paid in Full Statement';
        amount = paymentAmountFormat(paidAmount || 0);
        break;
      case DocumentType.PartialPayoffConfirmation:
        title = 'Partial Payoff Confirmation';
        break;
      case DocumentType.LenderUpload:
      case DocumentType.BorrowerUpload:
        // eslint-disable-next-line prefer-destructuring
        title = (document as UploadDocument).title;
        notificationType = 'manuallyUploaded';
        break;
      case DocumentType.Form1098: {
        const { isCorrected, year } = document as Form1098Document;
        title = `${isCorrected ? 'CORRECTED: ' : ''}${year} Form 1098`;
        break;
      }
      case DocumentType.EscrowAnalysis: {
        const { isCorrected, effectiveDate } = document as EscrowAnalysisDocument;
        title = `${isCorrected ? 'CORRECTED: ' : ''}Escrow Analysis (effective ${dateFormat(
          effectiveDate,
          'LLL yyyy',
        )})`;
        break;
      }
      case DocumentType.DefermentWorkoutPlan:
        title = 'Deferment Workout Agreement';
        break;
      case DocumentType.DelinquentInterestApplied:
        title = 'Default Interest Applied';
        break;
      case DocumentType.PrincipalModificationApplied:
        title = 'Principal Modification Applied';
        break;
      case DocumentType.PrincipalModificationCancelled:
        title = 'Principal Modification Cancelled';
        break;
      case DocumentType.PreActiveWelcome:
        title = EMAIL_TITLES.welcome;
        break;
      case DocumentType.VerificationOfMortgage:
        title = 'Verification of Mortgage';
        break;
      case DocumentType.LoanExtensionAgreement:
        title = 'Loan Extension Agreement';
        break;
      case DocumentType.LoanExtensionFees:
        title = 'Loan Extension Fees';
        break;
      case DocumentType.FlexModCancellationNotice:
        title = 'Flex Modification Cancellation Notice';
        break;
      case DocumentType.ForbearanceWorkoutPlanCompleted:
        title = 'Forbearance Period Completed';
        break;
      case DocumentType.ForbearanceWorkoutPlanEarlyTerminationNotice:
        title = 'Forbearance Period Early Termination Notice';
        break;
      case DocumentType.RepaymentPlanOfferNotice:
        title = 'Repayment Plan Offer Notice';
        break;
      case DocumentType.RepaymentPlanCancellationNotice:
        title = 'Repayment Plan Cancellation Notice';
        break;
      case DocumentType.MortgageInsuranceCancellationNotice:
        title = 'Mortgage Insurance Cancellation Notice';
        break;
      case DocumentType.MortgageProofOfClaim:
        title = 'Mortgage Proof of Claim';
        break;
      case DocumentType.PaymentDeferralOfferLetter:
        title = 'Payment Deferral Offer Letter';
        break;
      case DocumentType.PaymentDeferralCancellationNotice:
        title = 'Payment Deferral Cancellation Notice';
        break;
      case DocumentType.InterestRateAdjustmentNotification:
        title = 'Interest Rate Adjustment';
        break;
      case DocumentType.AccelerationLetter:
        title = 'Acceleration Letter';
        break;
      default:
        assertNever(type);
    }

    return {
      id,
      documentType: 'Document' as const,
      documentSpecificationType: type,
      notificationType,
      title,
      subtitle,
      callouts,
      sortByDate,
      createdAt: new Date(createdAt),
      date: createdAt,
      amount,
      version,
      isArchived,
    };
  });
}
